/**
 * Staffing Request → Pod change planner (docs/specs/staffing-requests.md). Pure, no I/O.
 *
 * A request is checked twice against the Solution's Pod: once when it is submitted (is
 * this a sensible ask of the Pod as it stands?) and again when the RM approves (is it
 * still sensible — has anyone changed the Pod in between?). Both checks run through
 * this module so they cannot disagree, and the approval path executes exactly the
 * operations it returns.
 *
 * Seats: R1…R4 are the Pod's Primary seats, bounded by its composition (Solo = R1,
 * Pair = R1–R2, Trio = R1–R3, Quad = R1–R4). An Add may fill an open seat inside that
 * bound, or the NEXT seat — which grows the Pod by one (Pair → Trio). Supporting is
 * unbounded. A Remove leaves the seat open; it never shrinks the Pod.
 *
 * Swap moves a Resource from ANOTHER Solution's Pod into a seat here. The seat may be
 * filled (the incumbent comes off, as a Replace) or open (as an Add, including the next
 * seat). The Resource and the seat they leave are fixed by the requester; approval seats
 * them here FIRST and only then ends their source assignment, so a failure part-way
 * leaves them double-seated (visible, recoverable) rather than on no Pod at all.
 */

export const SEAT_ROLES = ["R1", "R2", "R3", "R4", "Supporting"] as const;
export type SeatRole = (typeof SEAT_ROLES)[number];
export type PrimaryRole = Exclude<SeatRole, "Supporting">;

const PRIMARY_ROLES: readonly PrimaryRole[] = ["R1", "R2", "R3", "R4"];
const COMPOSITIONS = ["Solo", "Pair", "Trio", "Quad"] as const;
export type Composition = (typeof COMPOSITIONS)[number];

export type StaffingRequestKind = "Add" | "Replace" | "Remove" | "Swap";

export interface PodMember {
  assignmentId: string;
  resourceId: string;
  role: SeatRole;
}

export interface PodState {
  composition: Composition;
  /** Active assignments on the Solution — Primaries and Supporting. */
  members: PodMember[];
}

export interface PlannableRequest {
  kind: StaffingRequestKind;
  /** The seat being filled (Add) or changed (Replace / Remove). */
  role: SeatRole;
  /** Replace / Remove: the assignment being ended. Swap: the seat's incumbent, or null. */
  targetAssignmentId: string | null;
}

/**
 * Swap: the swapped-in Resource's seat on its source Pod, as it stands now. `active` false
 * (or the whole thing null) means that assignment has already ended.
 */
export interface SourceSeat {
  assignmentId: string;
  solutionId: string;
  resourceId: string;
  role: SeatRole;
  active: boolean;
}

export interface CandidateResource {
  id: string;
  active: boolean;
}

/**
 * One write an approval makes. Primary ops carry the assignment that must hold the seat
 * when the write runs (null = empty), so a concurrent edit fails the write instead of
 * being silently overwritten.
 */
export type StaffingOp =
  | { op: "expandPod"; to: Composition }
  | {
      op: "assignPrimary";
      role: PrimaryRole;
      resourceId: string;
      expectedIncumbentAssignmentId: string | null;
    }
  | { op: "unassignPrimary"; role: PrimaryRole; assignmentId: string }
  | { op: "assignSupporting"; resourceId: string }
  | { op: "unassignSupporting"; assignmentId: string }
  // Swap only: end the moved Resource's seat on its SOURCE Solution — the one op that
  // writes to a Solution other than the request's own, so it names that Solution.
  | { op: "vacatePrimary"; solutionId: string; role: PrimaryRole; assignmentId: string }
  | { op: "vacateSupporting"; solutionId: string; assignmentId: string };

export type PlanResult = { ok: true; ops: StaffingOp[] } | { ok: false; reason: string };

export function isPrimaryRole(role: SeatRole): role is PrimaryRole {
  return role !== "Supporting";
}

/** Seats a composition holds (Solo = 1 … Quad = 4). */
export function seatCount(composition: Composition): number {
  return COMPOSITIONS.indexOf(composition) + 1;
}

/** The 1-based seat number of a Primary role (R1 = 1 … R4 = 4). */
function seatNumber(role: PrimaryRole): number {
  return PRIMARY_ROLES.indexOf(role) + 1;
}

function compositionFor(seats: number): Composition | null {
  return COMPOSITIONS[seats - 1] ?? null;
}

export interface AddSeatOption {
  role: SeatRole;
  /** Set when choosing this seat grows the Pod — the composition it grows to. */
  expandsTo: Composition | null;
}

/**
 * Where an Add can go on this Pod: every open Primary seat, the next seat (growing the
 * Pod by one, up to Quad), and Supporting.
 */
export function addSeatOptions(pod: PodState): AddSeatOption[] {
  const seats = seatCount(pod.composition);
  const filled = new Set(pod.members.map((m) => m.role));
  const options: AddSeatOption[] = [];
  for (const role of PRIMARY_ROLES) {
    const n = seatNumber(role);
    if (n <= seats && !filled.has(role)) options.push({ role, expandsTo: null });
    if (n === seats + 1) options.push({ role, expandsTo: compositionFor(n) });
  }
  options.push({ role: "Supporting", expandsTo: null });
  return options;
}

/**
 * Validate a request as it is SUBMITTED. The requester's suggested Resource is optional
 * and is checked separately (it is only a suggestion), so this looks at the seat only.
 */
export function validateRequestAgainstPod(
  request: PlannableRequest,
  pod: PodState,
): { ok: true } | { ok: false; reason: string } {
  if (request.kind === "Swap" && request.targetAssignmentId === null) {
    const open = addSeatOptions(pod).some((o) => o.role === request.role);
    return open
      ? { ok: true }
      : {
          ok: false,
          reason: `${request.role} is not an open seat on this ${pod.composition} Pod. Pick a filled seat, an open seat, the next seat, or Supporting.`,
        };
  }
  if (request.kind === "Add") {
    if (request.targetAssignmentId !== null) {
      return { ok: false, reason: "An Add request does not name a Pod member." };
    }
    const allowed = addSeatOptions(pod).some((o) => o.role === request.role);
    return allowed
      ? { ok: true }
      : {
          ok: false,
          reason: `${request.role} is not an open seat on this ${pod.composition} Pod. Pick an open seat, the next seat, or Supporting.`,
        };
  }
  const target = pod.members.find((m) => m.assignmentId === request.targetAssignmentId);
  if (!target) {
    return { ok: false, reason: "That Resource is no longer on this Solution's Pod." };
  }
  if (target.role !== request.role) {
    return { ok: false, reason: "That Pod member has moved seats since the request was made." };
  }
  return { ok: true };
}

/**
 * Plan the Pod change an APPROVAL makes, against the Pod as it stands now.
 *
 * `resource` is the Resource the RM is placing (required for Add / Replace, ignored for
 * Remove). Refuses — never guesses — when the Pod has moved under the request: the
 * target already left, the seat was filled, the Pod shrank past the seat.
 *
 * `source` is a Swap's source seat as it stands now (ignored for every other kind).
 */
export function planStaffingChange(
  request: PlannableRequest,
  pod: PodState,
  resource: CandidateResource | null,
  source: SourceSeat | null = null,
): PlanResult {
  if (request.kind !== "Remove") {
    if (!resource) return { ok: false, reason: "Choose the Resource to place on the Pod." };
    if (!resource.active) return { ok: false, reason: "That Resource is inactive." };
  }

  if (request.kind === "Swap") {
    if (!resource) return { ok: false, reason: "Choose the Resource to place on the Pod." };
    if (!source?.active) {
      return {
        ok: false,
        reason:
          "The Resource has already left the seat they were moving from. Reject this request instead.",
      };
    }
    if (source.resourceId !== resource.id) {
      return { ok: false, reason: "That seat no longer belongs to the Resource being moved." };
    }
    // Placing them reuses the Add (open seat) or Replace (filled seat) rules exactly, so a
    // Swap can never seat someone where those two would refuse.
    const placement = planStaffingChange(
      { ...request, kind: request.targetAssignmentId === null ? "Add" : "Replace" },
      pod,
      resource,
    );
    if (!placement.ok) return placement;
    const vacate: StaffingOp = isPrimaryRole(source.role)
      ? {
          op: "vacatePrimary",
          solutionId: source.solutionId,
          role: source.role,
          assignmentId: source.assignmentId,
        }
      : {
          op: "vacateSupporting",
          solutionId: source.solutionId,
          assignmentId: source.assignmentId,
        };
    return { ok: true, ops: [...placement.ops, vacate] };
  }

  if (request.kind === "Add") {
    if (!resource) return { ok: false, reason: "Choose the Resource to place on the Pod." };
    if (pod.members.some((m) => m.resourceId === resource.id)) {
      return { ok: false, reason: "That Resource is already on this Solution." };
    }
    if (!isPrimaryRole(request.role)) {
      return { ok: true, ops: [{ op: "assignSupporting", resourceId: resource.id }] };
    }
    const seats = seatCount(pod.composition);
    const n = seatNumber(request.role);
    if (n > seats + 1) {
      return {
        ok: false,
        reason: `The Pod is now a ${pod.composition}; ${request.role} is no longer the next seat.`,
      };
    }
    if (pod.members.some((m) => m.role === request.role)) {
      return {
        ok: false,
        reason: `${request.role} has been filled since this request was made. Reject it, or ask for a Replace.`,
      };
    }
    const ops: StaffingOp[] = [];
    const grown = n > seats ? compositionFor(n) : null;
    if (grown) ops.push({ op: "expandPod", to: grown });
    ops.push({
      op: "assignPrimary",
      role: request.role,
      resourceId: resource.id,
      expectedIncumbentAssignmentId: null,
    });
    return { ok: true, ops };
  }

  const target = pod.members.find((m) => m.assignmentId === request.targetAssignmentId);
  if (!target) {
    return {
      ok: false,
      reason: "The Resource this request is about has already left the Pod. Reject it instead.",
    };
  }
  if (target.role !== request.role) {
    return { ok: false, reason: "That Pod member has moved seats since the request was made." };
  }

  if (request.kind === "Remove") {
    return {
      ok: true,
      ops: [
        isPrimaryRole(target.role)
          ? { op: "unassignPrimary", role: target.role, assignmentId: target.assignmentId }
          : { op: "unassignSupporting", assignmentId: target.assignmentId },
      ],
    };
  }

  // Replace.
  if (!resource) return { ok: false, reason: "Choose the Resource to place on the Pod." };
  if (resource.id === target.resourceId) {
    return { ok: false, reason: "The replacement is the Resource already in that seat." };
  }
  if (pod.members.some((m) => m.resourceId === resource.id)) {
    return { ok: false, reason: "That Resource is already on this Solution." };
  }
  if (isPrimaryRole(target.role)) {
    // assignResource ends the incumbent and seats the replacement in one transaction.
    return {
      ok: true,
      ops: [
        {
          op: "assignPrimary",
          role: target.role,
          resourceId: resource.id,
          expectedIncumbentAssignmentId: target.assignmentId,
        },
      ],
    };
  }
  // Supporting: add the replacement first so a failure leaves the incumbent in place.
  return {
    ok: true,
    ops: [
      { op: "assignSupporting", resourceId: resource.id },
      { op: "unassignSupporting", assignmentId: target.assignmentId },
    ],
  };
}

/**
 * Did an approval's change land? Used when an operation throws AFTER its write committed
 * (the assignment functions recompute Fit Score after commit): if the Pod already shows
 * the outcome, the request is Approved — putting it back to Pending would leave a done
 * change looking undone, and re-approving it would then refuse.
 */
export function changeLanded(
  request: PlannableRequest,
  pod: PodState,
  resourceId: string | null,
  /** Swap: is the moved Resource's source assignment still active? */
  sourceStillActive = false,
): boolean {
  if (request.kind === "Swap" && sourceStillActive) return false;
  const targetGone =
    request.targetAssignmentId === null ||
    !pod.members.some((m) => m.assignmentId === request.targetAssignmentId);
  if (request.kind === "Remove") return targetGone;
  const placed =
    resourceId !== null &&
    pod.members.some((m) => m.resourceId === resourceId && m.role === request.role);
  return placed && targetGone;
}
